import * as React from 'react';

import {
  GetCatalogProductViewQuery,
  ProductIdType,
} from '@notino/shared/definitions/types';

import { IClientRedirectProps } from '@components/ClientRedirect';
import { ProductSlider } from '@components/ProductSlider';
import { useGetProductItemProps } from '@components/Universals/ProductItem/useGetProductItemProps';
import {
  useTrackToCartAsync,
  useTrackToCartFailedAsync,
} from '@helpers/googleTagManager/vpProductCartTracking';

import { ProductsSectionPlaceholder } from '../../ProductsSection/ProductsSectionPlaceholder';
import { useProductsByIdsPaginated } from '../../ProductsSection/useProductsByIdsBatched';
import { useTrackExtras } from '../hooks/useTrackExtras';

import { ShowOtherVariantsLink } from './components/ShowOtherVariants';
import { SliderWrapper } from './styled';

const GTM_LIST_LABEL = 'accessories';
const ITEMS_PER_SLIDE = 3;

type AccessoriesContentProps = {
  accessories: NonNullable<
    NonNullable<
      GetCatalogProductViewQuery['productDetailByCatalogMasterId']
    >['features']['accessories']
  >;
};

export const AccessoriesContent: React.FC<AccessoriesContentProps> = ({
  accessories,
}) => {
  const mobileSliderRef = React.useRef<HTMLDivElement>();

  const catalogIds = (accessories.products ?? []).map(
    (product) => `${product.catalogId ?? ''}`
  );

  const { data, loading, loadMore } = useProductsByIdsPaginated(catalogIds, {
    idType: ProductIdType.Catalog,
    pageSize: ITEMS_PER_SLIDE * 2,
  });

  const { onNewItemShow, onSlideChange } = useTrackExtras({
    catalogIds,
    listName: GTM_LIST_LABEL,
    trackingEventAction: 'accessories_slide',
    trackingEventName: 'accessories_slide',
    trackingEventType: 'accessories',
    data,
    wasScrollLeft: mobileSliderRef.current?.scrollLeft === 0,
  });

  const trackAddToCartFailedAsync = useTrackToCartFailedAsync(
    catalogIds,
    GTM_LIST_LABEL,
    true
  );
  const trackAddToCardAsync = useTrackToCartAsync(
    catalogIds,
    GTM_LIST_LABEL,
    true
  );

  const getProductItemProps = useGetProductItemProps({
    noPriceIfUnavailable: false,
    useMasterUrl: true,
    wishlistActionLocation: 'list',
    showBuyButton: true,
    showAddToCartModal: false,
    onProductAddFailed: trackAddToCartFailedAsync,
    onProductAdded: trackAddToCardAsync,
    GTMEventLabel: GTM_LIST_LABEL,
    sendGTM: true,
  });

  const products =
    data
      .map((product, index) => {
        const hasOtherVariants = product?.attributes?.HasOtherVariants;

        return getProductItemProps({
          product,
          position: index + 1,
          appendComponent: hasOtherVariants && (
            <ShowOtherVariantsLink
              product={product as IClientRedirectProps['product']}
            />
          ),
          shouldHideItemBuyButton: hasOtherVariants,
        });
      })
      .filter((item): item is NonNullable<typeof item> => Boolean(item)) ?? [];

  if (!data || loading) {
    return (
      <ProductsSectionPlaceholder
        itemsPerSlide={ITEMS_PER_SLIDE}
        listLabel={GTM_LIST_LABEL}
        rows={5}
      />
    );
  }

  return (
    <div>
      <SliderWrapper centerSlider={products.length < ITEMS_PER_SLIDE}>
        <ProductSlider
          itemsPerSlide={ITEMS_PER_SLIDE}
          mobileScrollContainerRef={
            mobileSliderRef as React.RefObject<HTMLDivElement>
          }
          products={products}
          inViewThreshold={0.55}
          onSlideChange={onSlideChange}
          onItemShow={({ product }) => {
            onNewItemShow(product.id);
            // we fetch 2 slides at once, so when last item of first slide is shown,
            // we fetch next page to prevent carousel glitch
            if (
              product.id ===
              data[Math.max(data.length - 1 - ITEMS_PER_SLIDE, 0)]?.id
            ) {
              loadMore();
            }
          }}
          fallback={
            <ProductsSectionPlaceholder
              itemsPerSlide={ITEMS_PER_SLIDE}
              listLabel={GTM_LIST_LABEL}
              rows={5}
            />
          }
        />
      </SliderWrapper>
    </div>
  );
};
